import "../styles/Home.css";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Ecosystem from "../components/Ecosystem";
import { Helmet } from "react-helmet-async";

export default function Home() {
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
  };

  return (
    <>
      <Helmet>
        <title>Almond Softwares | Enterprise Web, Salesforce CRM & Flagship Android Apps</title>
        <meta
          name="description"
          content="Almond Softwares engineers high-performance web applications, Shopify stores, Salesforce CRM automation, and flagship Android apps including DocNo (₹10) and Forge Photo Studio."
        />
        <meta
          name="keywords"
          content="Salesforce developer India, Shopify Plus development company, DocNo app, Forge photo editor, React development services, Almond Software"
        />
      </Helmet>

      {/* AMBIENT CAUSTIC BACKGROUND */}
      <div className="bg-ambient-canvas" aria-hidden="true">
        <div className="ambient-orb orb-1"></div>
        <div className="ambient-orb orb-2"></div>
        <div className="ambient-orb orb-3"></div>
      </div>

      {/* ================= HERO SECTION ================= */}
      <header className="hero">
        <div className="container hero-grid">
          <div className="hero-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="hero-kicker"
            >
              <span>✦</span> MODERN SOFTWARE ENGINEERING & ANDROID APPS
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="hero-title"
            >
              Building <span className="gradient-text">High-Performance</span> Software & Flagship Mobile Apps
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="hero-description"
            >
              Almond Software designs and develops modern web applications, custom Shopify stores, Salesforce CRM automation, and thoughtful Android applications built with speed, security, and precision.
            </motion.p>

            <motion.div
              className="hero-buttons"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Link to="/services" className="btn-primary">
                Explore Services <span>→</span>
              </Link>
              <Link to="/apps" className="btn-secondary">
                Flagship Apps (DocNo ₹10) <span>→</span>
              </Link>
            </motion.div>

            <motion.div
              className="trust-badges-row"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              <div className="msme-badge">
                <span className="blue-tick">✔</span>
                <span>MSME Registered Enterprise</span>
              </div>
              <div className="msme-badge">
                <span className="blue-tick">★</span>
                <span>Google Play Verified Developer</span>
              </div>
              <div className="msme-badge">
                <span className="blue-tick">🛡️</span>
                <span>100% Privacy-First Architecture</span>
              </div>
            </motion.div>
          </div>

          {/* Spatial Holographic Core */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
          >
            <Ecosystem />
          </motion.div>
        </div>
      </header>

      {/* ================= TRUST SECTION ================= */}
      <section className="trust">
        <div className="container">
          <h2 className="trust-title">Technologies & Enterprise Standards Trusted Globally</h2>

          <motion.div
            className="trust-logos"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="trust-logo-pill">🛍️ Shopify Plus</div>
            <div className="trust-logo-pill">☁️ Salesforce CRM</div>
            <div className="trust-logo-pill">⚛️ React 19 Ecosystem</div>
            <div className="trust-logo-pill">🤖 Kotlin Android Native</div>
            <div className="trust-logo-pill">⚡ Cloudflare Edge</div>
            <div className="trust-logo-pill">🧠 OpenAI & On-Device AI</div>
          </motion.div>
        </div>
      </section>

      {/* ================= PROCESS SECTION ================= */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: "center" }}>
            <span className="section-badge">ENGINEERING PROCESS</span>
            <h2 className="section-title">How We Build & Deliver</h2>
            <p className="section-subtitle">
              A systematic, engineering-focused approach to delivering reliable software, robust automations, and fast digital experiences.
            </p>
          </div>

          <div className="process">
            <div className="process-card">
              <span className="process-num">PHASE 01</span>
              <h3>Architecture & Planning</h3>
              <p>We analyze project requirements, existing tech stacks, and user workflows to design clean, maintainable technical roadmaps.</p>
            </div>

            <div className="process-card">
              <span className="process-num">PHASE 02</span>
              <h3>High-Velocity Engineering</h3>
              <p>Developing modern, accessible web interfaces and robust backend APIs with sub-second page loads and clean code.</p>
            </div>

            <div className="process-card">
              <span className="process-num">PHASE 03</span>
              <h3>Workflow & Process Automation</h3>
              <p>Integrating Salesforce CRM flows, automated lead routing, and dynamic customer engagement triggers.</p>
            </div>

            <div className="process-card">
              <span className="process-num">PHASE 04</span>
              <h3>Reliable Scaling & Support</h3>
              <p>Deploying distributed cloud infrastructure, edge caching, and optimized queries to handle growth smoothly.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SERVICES SECTION ================= */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: "center" }}>
            <span className="section-badge">FULL-STACK CAPABILITIES</span>
            <h2 className="section-title">Enterprise Software Development</h2>
            <p className="section-subtitle">
              We engineer scalable web applications, eCommerce systems, CRM pipelines, and AI workflows using hardened enterprise technology.
            </p>
          </div>

          <motion.div
            className="services"
            variants={containerVariants}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <motion.div className="service-card" variants={itemVariants}>
              <div className="service-card-icon">⚛️</div>
              <h3>Custom Web & React Platforms</h3>
              <p>High-performance single-page applications, interactive enterprise dashboards, and SEO-dominant web architectures built on modern React.</p>
              <div className="service-features-list">
                <div className="service-feature-item"><span>✓</span> Sub-second Lighthouse 99+ Speed</div>
                <div className="service-feature-item"><span>✓</span> Micro-Frontends & Design Systems</div>
                <div className="service-feature-item"><span>✓</span> Full SEO Semantic Structured Data</div>
              </div>
            </motion.div>

            <motion.div className="service-card" variants={itemVariants}>
              <div className="service-card-icon">🛍️</div>
              <h3>Shopify Plus eCommerce</h3>
              <p>Conversion-focused custom Shopify Liquid themes, headless storefronts, frictionless checkout flows, and custom private apps.</p>
              <div className="service-features-list">
                <div className="service-feature-item"><span>✓</span> High-Converting Mobile UX</div>
                <div className="service-feature-item"><span>✓</span> ERP & Inventory Sync Pipelines</div>
                <div className="service-feature-item"><span>✓</span> Zero-Bloat Performance Themes</div>
              </div>
            </motion.div>

            <motion.div className="service-card" variants={itemVariants}>
              <div className="service-card-icon">☁️</div>
              <h3>Salesforce CRM Architecture</h3>
              <p>Custom Apex development, Lightning Web Components (LWC), end-to-end integration with third-party software, and automated lead-to-cash workflows.</p>
              <div className="service-features-list">
                <div className="service-feature-item"><span>✓</span> Complex Apex & LWC Development</div>
                <div className="service-feature-item"><span>✓</span> Multi-System API Integrations</div>
                <div className="service-feature-item"><span>✓</span> 6× Certified Salesforce Experts</div>
              </div>
            </motion.div>

            <motion.div className="service-card" variants={itemVariants}>
              <div className="service-card-icon">⚡</div>
              <h3>API & Distributed Cloud Infra</h3>
              <p>Ultra-secure REST & GraphQL endpoints, serverless edge workers on Cloudflare, and resilient cloud architectures configured for zero downtime.</p>
              <div className="service-features-list">
                <div className="service-feature-item"><span>✓</span> Sub-10ms Global Edge Routing</div>
                <div className="service-feature-item"><span>✓</span> High-Concurrency Load Balancing</div>
                <div className="service-feature-item"><span>✓</span> SOC2 & GDPR Compliant Security</div>
              </div>
            </motion.div>

            <motion.div className="service-card" variants={itemVariants}>
              <div className="service-card-icon">🧠</div>
              <h3>AI & Automation Workflows</h3>
              <p>Custom LLM agents, predictive analytics models, on-device intelligence, and smart workflow automation that eliminate redundant human operational hours.</p>
              <div className="service-features-list">
                <div className="service-feature-item"><span>✓</span> Private LLM Document Querying</div>
                <div className="service-feature-item"><span>✓</span> Real-Time Speech & Vision Models</div>
                <div className="service-feature-item"><span>✓</span> Autonomous Backend Task Agents</div>
              </div>
            </motion.div>

            <motion.div className="service-card" variants={itemVariants}>
              <div className="service-card-icon">📱</div>
              <h3>Native Mobile Engineering</h3>
              <p>Award-winning Kotlin Android applications designed with hardware-accelerated graphics, offline-first SQLite databases, and strict zero-telemetry privacy.</p>
              <div className="service-features-list">
                <div className="service-feature-item"><span>✓</span> Material You & Liquid Morphic UX</div>
                <div className="service-feature-item"><span>✓</span> 100% Offline Encrypted Vaults</div>
                <div className="service-feature-item"><span>✓</span> Google Play Storefront Optimization</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ================= FLAGSHIP MOBILE APPS ECOSYSTEM ================= */}
      <section className="section home-apps-spotlight">
        <div className="container">
          <div className="home-apps-header">
            <span className="home-apps-badge">MOBILE ECOSYSTEM · GOOGLE PLAY CATALOG</span>
            <h2 className="section-title">Flagship Android Applications</h2>
            <p className="section-subtitle">
              Engineered in-house by Almond Software with retina liquid morphism aesthetics, hardware acceleration, zero telemetry tracking, and privacy-first local processing.
            </p>
          </div>

          <div className="home-apps-grid">
            {/* DocNo */}
            <div className="home-app-card docno-card">
              <div className="home-app-top">
                <span className="home-app-icon">📝</span>
                <span className="home-app-tag">₹10 Only · Notes & Vault</span>
              </div>
              <h3>DocNo – Notes, Expenses & Docs</h3>
              <p>
                Smart markdown notes, monthly budget expense tracking, receipt attachments, and a 100% encrypted offline document vault. Zero advertisements and lifetime access for only ₹10.
              </p>
              <div className="home-app-features">
                <span>✓ ₹10 One-Time Lifetime</span>
                <span>✓ 100% Offline Vault</span>
                <span>✓ Expense & Budget Charts</span>
                <span>✓ Zero Ads & No Tracking</span>
              </div>
              <div className="home-app-actions">
                <Link to="/apps#docno" className="home-app-learn-btn">
                  Explore DocNo →
                </Link>
                <a
                  href="https://play.google.com/store/apps/details?id=com.almondsoftwares.docno"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-app-store-link"
                >
                  Get on Google Play (₹10) ↗
                </a>
              </div>
            </div>

            {/* Forge */}
            <div className="home-app-card forge-card">
              <div className="home-app-top">
                <span className="home-app-icon">🎨</span>
                <span className="home-app-tag">Free · Photo Studio</span>
              </div>
              <h3>Forge – Photo Editor</h3>
              <p>
                Desktop-grade creative studio for Android featuring multi-layer image stacking, 24 Photoshop-grade blend modes, RGB tone curves, and on-device AI masking.
              </p>
              <div className="home-app-features">
                <span>✓ 24 Blend Modes</span>
                <span>✓ RGB Tone Curves</span>
                <span>✓ AI Sky Masking</span>
                <span>✓ Free on Google Play</span>
              </div>
              <div className="home-app-actions">
                <Link to="/apps#forge" className="home-app-learn-btn">
                  Explore Forge →
                </Link>
                <a
                  href="https://play.google.com/store/apps/details?id=com.almondsoftwares.forge"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-app-store-link"
                >
                  Google Play ↗
                </a>
              </div>
            </div>

            {/* Video Player */}
            <div className="home-app-card video-card">
              <div className="home-app-top">
                <span className="home-app-icon">🎬</span>
                <span className="home-app-tag">Free · Media Engine</span>
              </div>
              <h3>Almond Video Player</h3>
              <p>
                Hardware-accelerated 4K/MKV video engine featuring a built-in stream downloader, HLS/DASH adaptive playback, intuitive gesture seeking, and subtitle auto-sync.
              </p>
              <div className="home-app-features">
                <span>✓ In-App Stream Downloader</span>
                <span>✓ 4K Ultra-HD Hardware Dec</span>
                <span>✓ Gesture Brightness & Volume</span>
                <span>✓ Free on Google Play</span>
              </div>
              <div className="home-app-actions">
                <Link to="/apps#video" className="home-app-learn-btn">
                  Explore Video →
                </Link>
                <a
                  href="https://play.google.com/store/apps/details?id=com.a.almondvideoplayer&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-app-store-link"
                >
                  Google Play ↗
                </a>
              </div>
            </div>

            {/* Music Player */}
            <div className="home-app-card music-card">
              <div className="home-app-top">
                <span className="home-app-icon">🎵</span>
                <span className="home-app-tag">Free · Audiophile Hi-Res</span>
              </div>
              <h3>Almond Music Player</h3>
              <p>
                Studio-grade audiophile player engineered with a 64-band graphic equalizer, 24-band parametric acoustic filters, live FFT spectrum analyzer, and bit-perfect lossless playback.
              </p>
              <div className="home-app-features">
                <span>✓ 64-Band Studio EQ</span>
                <span>✓ Live FFT Spectrum Visualizer</span>
                <span>✓ Lossless FLAC / DSD</span>
                <span>✓ Free on Google Play</span>
              </div>
              <div className="home-app-actions">
                <Link to="/apps#music" className="home-app-learn-btn">
                  Explore Music →
                </Link>
                <a
                  href="https://play.google.com/store/apps/details?id=com.a.almondmusicplayer&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="home-app-store-link"
                >
                  Google Play ↗
                </a>
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: "44px" }}>
            <Link to="/apps" className="btn-primary" style={{ padding: "16px 40px", fontSize: "16px" }}>
              Explore All 4 Apps in Dedicated Showcase (Specs & Mockups) →
            </Link>
          </div>
        </div>
      </section>

      {/* ================= TECH STACK SECTION ================= */}
      <section className="section">
        <div className="container">
          <div style={{ textAlign: "center" }}>
            <span className="section-badge">CORE TECHNOLOGIES</span>
            <h2 className="section-title">Engineering Stack</h2>
            <p className="section-subtitle">
              We leverage industry-proven languages, frameworks, and cloud primitives to build unbreakable enterprise software.
            </p>
          </div>

          <div className="tech-stack">
            <div className="tech">☁️ Salesforce Apex & LWC</div>
            <div className="tech">⚛️ React 19 & Next.js</div>
            <div className="tech">🛍️ Shopify Liquid & GraphQL</div>
            <div className="tech">🤖 Kotlin Android Native</div>
            <div className="tech">🐍 Python & FastAPI</div>
            <div className="tech">⚡ Cloudflare Workers & CDN</div>
            <div className="tech">🔥 Firebase & Firestore</div>
            <div className="tech">🐳 Docker & Microservices</div>
          </div>
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="footer">
        <div className="container">
          <div className="footer-top-grid">
            <div className="footer-brand">
              <h2>Almond Software</h2>
              <p>
                Architecting high-concurrency web platforms, enterprise Salesforce automation, Shopify Plus systems, and flagship Android applications.
              </p>
              <div style={{ marginTop: "16px", display: "inline-flex", gap: "8px", alignItems: "center", fontSize: "13px", color: "#94a3b8" }}>
                <span style={{ color: "#10b981" }}>✔</span> MSME Registered Enterprise (Govt. of India)
              </div>
            </div>

            <div className="footer-col">
              <h4>Android Ecosystem</h4>
              <div className="footer-links">
                <Link to="/apps#docno">DocNo (Notes & Expenses ₹10)</Link>
                <Link to="/apps#forge">Forge (Photo Studio)</Link>
                <Link to="/apps#video">Almond Video Player</Link>
                <Link to="/apps#music">Almond Music Player</Link>
                <a href="https://play.google.com/store/apps/developer?id=ALMOND+SOFTWARES" target="_blank" rel="noopener noreferrer">
                  Google Play Developer Page ↗
                </a>
              </div>
            </div>

            <div className="footer-col">
              <h4>Enterprise Services</h4>
              <div className="footer-links">
                <Link to="/services">Software Development</Link>
                <Link to="/shopify">Shopify Store Architecture</Link>
                <Link to="/salesforce">Salesforce CRM Automation</Link>
                <Link to="/creations">Recent Works & Case Studies</Link>
                <Link to="/about">About Almond Software</Link>
              </div>
            </div>

            <div className="footer-col">
              <h4>Connect & Inquiries</h4>
              <div className="footer-links">
                <Link to="/contact">Contact Our Engineering Team</Link>
                <Link to="/website-development-company-raipur">Raipur Hub</Link>
                <Link to="/website-development-company-nagpur">Nagpur Hub</Link>
                <Link to="/Founder">Founder Profile</Link>
                <Link to="/blogs">Engineering Blog</Link>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <div>© {new Date().getFullYear()} Almond Software. All rights reserved. Designed for retina displays with liquid morphism.</div>
            <div style={{ display: "flex", gap: "20px" }}>
              <span>Privacy-First</span>
              <span>•</span>
              <span>Zero Ads in DocNo</span>
              <span>•</span>
              <span>Enterprise Grade</span>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}